// BHP-1087: thin VPS read-model over Backend2.0 `GET /api/vps` (VpsOutputDto).
//
// The table holds only slow-moving facts — identity, hostname, labels, owner.
// Live state (status, IP, server type, created) is deliberately absent: it
// would be stale the moment it was stored. BHP-1067 keeps the rows in step with
// Hetzner, and BHP-1101 serves the live state read-through via
// `GET /api/vps/{uuid}/server` for the detail page.
export interface VpsSummary {
  uuid: string;
  hostname: string;
  hetznerId: number | null;
  labels: Record<string, string>;
  customerUuid: string | null;
  /**
   * BHP-1118: the Hetzner project the server runs in, off the account on the
   * row (BHP-1067). Optional: a row the sync has not claimed yet has none, and
   * older API responses predate the field. Absent and null are treated the same
   * — the server lands in the "unknown project" group.
   */
  hetznerProjectName?: string | null;
}

/**
 * Hetzner label key that discriminates the product types, carried over from the
 * legacy backend (`Servers::SERVER_TYPE_LABEL`). The three legacy endpoints
 * (`/vps|wordpress|linked-helper/hosting/servers`) filtered on exactly this
 * label; Backend2.0 exposes one `/api/vps` collection, so the frontend filters.
 */
export const SERVER_TYPE_LABEL = 'server-type';

export type VpsServerType = 'generic' | 'wordpress' | 'linked-helper';

/** The `server-type` label value, or undefined when unlabelled. */
export function serverTypeOf(vps: VpsSummary): string | undefined {
  return vps.labels?.[SERVER_TYPE_LABEL];
}

/**
 * BHP-1102: unlabelled servers count as `generic`.
 *
 * `server-type` is our own product split, written by the backend when it
 * creates a server. A machine someone started in the Hetzner console never got
 * one, and nothing in Hetzner's payload says whether it is a WordPress or a
 * Linked Helper box — its own `server_type` (cx22, cpx31) is hardware sizing.
 *
 * The legacy behaviour was an exact match, which was harmless while every
 * server came from us. Since BHP-1067 syncs the servers that were created
 * outside the platform, an exact match hides them from every list: the customer
 * then sees an incomplete list of their own servers with nothing indicating
 * anything is missing. Generic is the catch-all so that cannot happen.
 */
export function filterByServerType(
  servers: VpsSummary[],
  type: VpsServerType
): VpsSummary[] {
  return servers.filter(
    (server) => (serverTypeOf(server) ?? 'generic') === type
  );
}

/** A set of servers that share one Hetzner project; `projectName` null = unknown. */
export interface VpsProjectGroup {
  projectName: string | null;
  servers: VpsSummary[];
}

/**
 * BHP-1118: group servers by their Hetzner project so a customer whose servers
 * span several projects can tell them apart on the overview.
 *
 * Groups are ordered by project name (case-insensitive); the "unknown" group —
 * rows with no linked project (absent or null) — always comes last so it reads
 * as the leftover bucket rather than jumping around alphabetically. Within a
 * group the incoming order is preserved (the backend already sorts the
 * collection), so grouping only reorders across projects, never within one.
 */
export function groupByProject(servers: VpsSummary[]): VpsProjectGroup[] {
  const groups = new Map<string | null, VpsSummary[]>();

  for (const server of servers) {
    const key = server.hetznerProjectName ?? null;
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [server]);
    } else {
      bucket.push(server);
    }
  }

  return [...groups.entries()]
    .map(([projectName, grouped]) => ({ projectName, servers: grouped }))
    .sort((a, b) => {
      if (a.projectName === null) return 1;
      if (b.projectName === null) return -1;
      return a.projectName.localeCompare(b.projectName);
    });
}
